import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useRegistrationSettings,
  useUpdateRegistrationSettings,
  type ConsentSettings,
} from '@/admin/hooks/use-registration-settings'

/**
 * Settings > Consent: the tenant's own T&C and marketing email boxes, shown on
 * every registration surface (Sage wizard and Olive). Saving a changed T&C
 * wording stamps a new version on the server, so each acceptance stays tied to
 * the words the customer saw. The route needs registration-settings.manage.
 */
export function ConsentSettingsTab() {
  const { t } = useTranslation()
  const { data: settings, isPlaceholderData, isFetching } = useRegistrationSettings()
  // Wait for the real settings so the form starts from the saved wording. A
  // copy persisted before consent settings existed has none until it refetches.
  if (isPlaceholderData || (!settings?.consent_settings && isFetching)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[var(--ink-3)]" size={20} />
      </div>
    )
  }
  // The settings query falls back to defaults (no consent block) on error.
  if (!settings?.consent_settings) {
    return (
      <p className="clara-small">
        {t('consent.settings.loadFailed', 'Could not load consent settings.')}
      </p>
    )
  }
  return <ConsentForm consents={settings.consent_settings} />
}

function ConsentForm({ consents }: { consents: ConsentSettings }) {
  const { t } = useTranslation()
  const update = useUpdateRegistrationSettings()
  const terms = consents.terms
  const marketing = consents.marketing_email

  const fields = useMemo<FormField[]>(() => {
    const wording = t('consent.settings.wording', 'Checkbox wording')
    const wordingRequired = t('consent.settings.wordingRequired', 'Wording is required')
    const purpose = t('consent.settings.purpose', 'How you use it (optional)')
    const termsOn = (ctx: { values: Record<string, unknown> }) => Boolean(ctx.values.terms_enabled)
    const marketingOn = (ctx: { values: Record<string, unknown> }) =>
      Boolean(ctx.values.marketing_enabled)
    return [
      {
        type: 'switch',
        name: 'terms_enabled',
        label: t('consent.settings.termsEnabled', 'Ask customers to accept your terms and conditions'),
        value: terms?.enabled ?? false,
        className: 'col-span-2',
        testId: 'consent-settings-terms-enabled',
      },
      {
        type: 'textarea',
        name: 'terms_label',
        label: wording,
        hint: t(
          'consent.settings.termsHint',
          'Link your terms like this: [terms and conditions](https://example.com/terms)',
        ),
        required: wordingRequired,
        value: terms?.label ?? '',
        visible: termsOn,
        className: 'col-span-2',
        testId: 'consent-settings-terms-label',
      },
      {
        type: 'text',
        name: 'terms_purpose',
        label: purpose,
        value: terms?.purpose ?? '',
        visible: termsOn,
        className: 'col-span-2',
        testId: 'consent-settings-terms-purpose',
      },
      {
        type: 'switch',
        name: 'terms_required',
        label: t('consent.settings.termsRequired', 'Customers must tick it to register'),
        value: terms?.required ?? false,
        visible: termsOn,
        className: 'col-span-2',
        testId: 'consent-settings-terms-required',
      },
      {
        type: 'switch',
        name: 'marketing_enabled',
        label: t('consent.settings.marketingEnabled', 'Offer marketing emails'),
        value: marketing?.enabled ?? false,
        className: 'col-span-2',
        testId: 'consent-settings-marketing-enabled',
      },
      {
        type: 'textarea',
        name: 'marketing_label',
        label: wording,
        required: wordingRequired,
        value: marketing?.label ?? '',
        visible: marketingOn,
        className: 'col-span-2',
        testId: 'consent-settings-marketing-label',
      },
      {
        type: 'text',
        name: 'marketing_purpose',
        label: purpose,
        value: marketing?.purpose ?? '',
        visible: marketingOn,
        className: 'col-span-2',
        testId: 'consent-settings-marketing-purpose',
      },
    ]
  }, [terms, marketing, t])

  const { Form, FormFooter, ref: formRef } = useForm(
    { name: 'registration-consent-form', submit: 'footer', fields },
    async (values) => {
      try {
        await update.mutateAsync({
          consent_settings: {
            terms: {
              enabled: Boolean(values.terms_enabled),
              required: Boolean(values.terms_required),
              label: String(values.terms_label ?? ''),
              purpose: String(values.terms_purpose ?? ''),
            },
            marketing_email: {
              enabled: Boolean(values.marketing_enabled),
              label: String(values.marketing_label ?? ''),
              purpose: String(values.marketing_purpose ?? ''),
            },
          },
        })
        toast.success(t('consent.settings.saved', 'Consent settings saved'))
      } catch (err) {
        toastApiError(err, t('consent.settings.saveFailed', 'Could not save consent settings.'))
      }
    },
  )

  return (
    <div className="max-w-[640px]" data-testid="settings-consent">
      <p className="clara-section-label mb-4">{t('consent.title', 'Consent')}</p>
      <p className="clara-small mb-4 max-w-[560px]">
        {t(
          'consent.settings.intro',
          'Checkboxes customers see when they register, in your admin and on your customer site. Boxes are never pre-ticked.',
        )}
      </p>
      <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-4">
        <Form className="grid grid-cols-2 gap-x-5 gap-y-4" />
        <p
          className="mt-3 text-[11px] text-[var(--ink-3)]"
          data-testid="consent-settings-terms-version"
        >
          {terms?.version
            ? t(
                'consent.settings.version',
                'T&C version {{version}}. Changing the wording starts a new version.',
                { version: terms.version },
              )
            : t(
                'consent.settings.noVersion',
                'A T&C version is set when you first save the wording.',
              )}
        </p>
        <div className="mt-4 flex justify-end border-t-[0.5px] border-[var(--rule)] pt-4">
          {FormFooter && (
            <FormFooter
              onCancel={() => formRef.current?.resetForm()}
              disabled={update.isPending}
              submitText={
                update.isPending
                  ? t('consent.settings.saving', 'Saving...')
                  : t('consent.settings.save', 'Save consent settings')
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}
